import { useEditorT } from '@i18n/editor';
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Undo2, Redo2, Loader2, Save, Trash2, ChevronDown, Minus, Plus, Square, Columns2 } from 'lucide-react';
import { YearbookElement, TextStyle } from '@/types/yearbook';
import { HexColorPicker } from 'react-colorful';
import { FONT_NAMES, ensureFontLoaded } from '@/lib/fonts';
import FontOptionButton from '../FontOptionButton';
import type { MobileViewMode } from './MobilePageView';

const fontOptions = FONT_NAMES;

interface MobileTopBarProps {
  onUndo: () => void;

  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onSubmit: () => void;
  isSaving: boolean;
  isWememberz: boolean;
  selectedElement?: YearbookElement | null;
  onUpdateElement?: (id: string, updates: Partial<YearbookElement>) => void;
  onDeleteElement?: (id: string) => void;
  viewMode?: MobileViewMode;
  onViewModeChange?: (mode: MobileViewMode) => void;
}

const MobileTopBar: React.FC<MobileTopBarProps> = ({
  onUndo,

  onRedo,
  canUndo,
  canRedo,
  isSaving,
  selectedElement,
  onUpdateElement,
  onDeleteElement,
  viewMode,
  onViewModeChange,
}) => {
  const { te } = useEditorT();
  const [showFontPicker, setShowFontPicker] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const fontRef = useRef<HTMLDivElement>(null);
  const colorRef = useRef<HTMLDivElement>(null);

  const isText = selectedElement?.type === 'text';
  const textStyle = selectedElement?.textStyle;
  const fontSize = textStyle?.fontSize ?? 18;
  const fontFamily = textStyle?.fontFamily ?? 'Arial';
  const color = textStyle?.color ?? '#1a1a2e';

  useEffect(() => { ensureFontLoaded(fontFamily); }, [fontFamily]);

  // Close pickers on outside click
  useEffect(() => {
    if (!showFontPicker && !showColorPicker) return;
    const handler = (e: MouseEvent) => {
      if (showFontPicker && fontRef.current && !fontRef.current.contains(e.target as Node)) setShowFontPicker(false);
      if (showColorPicker && colorRef.current && !colorRef.current.contains(e.target as Node)) setShowColorPicker(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showFontPicker, showColorPicker]);

  // Reset pickers when element changes
  useEffect(() => {
    setShowFontPicker(false);
    setShowColorPicker(false);
  }, [selectedElement?.id]);

  const updateTextStyle = (updates: Partial<TextStyle>) => {
    if (!selectedElement || !onUpdateElement) return;
    onUpdateElement(selectedElement.id, {
      textStyle: { ...textStyle, ...updates } as TextStyle,
    });
  };

  const changeSize = (delta: number) => {
    const newSize = Math.max(8, Math.min(200, fontSize + delta));
    updateTextStyle({ fontSize: newSize });
  };

  const getFloatingStyle = (anchor: HTMLDivElement | null, width: number, align: 'left' | 'right' = 'left'): React.CSSProperties => {
    if (!anchor || typeof window === 'undefined') return { position: 'fixed', top: 48, left: 8, zIndex: 1000, width };
    const rect = anchor.getBoundingClientRect();
    const rawLeft = align === 'right' ? rect.right - width : rect.left;
    const left = Math.max(8, Math.min(rawLeft, window.innerWidth - width - 8));
    return { position: 'fixed', top: rect.bottom + 4, left, zIndex: 1000, width };
  };

  return (
    <header className="h-11 bg-editor-topbar flex items-center px-2 border-b border-editor-sidebar-border shrink-0 gap-1 overflow-x-auto relative z-[200]" style={{ scrollbarWidth: 'none' }}>
      {/* Element tools */}

      {selectedElement && onUpdateElement && onDeleteElement && (
        <>
          {isText && (
            <>
              {/* Font family */}
              <div className="relative shrink-0" ref={fontRef}>
                <button
                  onClick={() => { setShowFontPicker(!showFontPicker); setShowColorPicker(false); }}
                  className="flex items-center gap-0.5 px-1.5 py-1 rounded bg-editor-sidebar-hover text-editor-sidebar-foreground text-[11px] max-w-[100px] truncate"
                >
                  <span className="truncate" style={{ fontFamily }}>{fontFamily}</span>
                  <ChevronDown size={12} className="shrink-0" />
                </button>
                {showFontPicker && createPortal(
                  <div
                    className="max-h-52 overflow-y-auto bg-editor-sidebar border border-editor-sidebar-border rounded-md shadow-lg"
                    style={getFloatingStyle(fontRef.current, 192)}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  >
                    {fontOptions.map((f) => (
                      <FontOptionButton
                        key={f}
                        font={f}
                        onSelect={() => { updateTextStyle({ fontFamily: f }); setShowFontPicker(false); }}
                        className={`w-full text-left px-3 py-1.5 text-xs hover:bg-editor-sidebar-hover transition-colors ${f === fontFamily ? 'text-editor-sidebar-active font-semibold' : 'text-editor-sidebar-foreground'}`}
                      />
                    ))}
                  </div>,
                  document.body
                )}
              </div>

              {/* Font size */}
              <div className="flex items-center gap-0 shrink-0">
                <button
                  onClick={() => changeSize(-2)}
                  className="p-1 rounded bg-editor-sidebar-hover text-editor-sidebar-foreground active:bg-editor-sidebar-active/20"
                >
                  <Minus size={14} />
                </button>
                <span className="text-[11px] text-editor-sidebar-foreground w-6 text-center tabular-nums">{fontSize}</span>
                <button
                  onClick={() => changeSize(2)}
                  className="p-1 rounded bg-editor-sidebar-hover text-editor-sidebar-foreground active:bg-editor-sidebar-active/20"
                >
                  <Plus size={14} />
                </button>
              </div>

              {/* Color */}
              <div className="relative shrink-0" ref={colorRef}>
                <button
                  onClick={() => { setShowColorPicker(!showColorPicker); setShowFontPicker(false); }}
                  className="w-6 h-6 rounded border border-editor-sidebar-border shrink-0"
                  style={{ backgroundColor: color }}
                />
                {showColorPicker && createPortal(
                  <div
                    className="p-2 bg-editor-sidebar border border-editor-sidebar-border rounded-md shadow-lg"
                    style={getFloatingStyle(colorRef.current, 216, 'right')}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  >
                    <HexColorPicker color={color} onChange={(c) => updateTextStyle({ color: c })} />
                  </div>,
                  document.body
                )}
              </div>

              <div className="w-px h-5 bg-editor-sidebar-border shrink-0 mx-0.5" />
            </>
          )}

          {/* Delete */}
          <button
            onClick={() => onDeleteElement(selectedElement.id)}
            className="p-1 rounded bg-red-500/20 text-red-400 active:bg-red-500/40 shrink-0"
          >
            <Trash2 size={16} />
          </button>

          <div className="w-px h-5 bg-editor-sidebar-border shrink-0 mx-0.5" />
        </>
      )}

      {/* Spacer */}
      <div className="flex-1 min-w-0" />

      {/* View mode: single / double page — hidden while an element is selected so the tools keep the room */}
      {viewMode && onViewModeChange && !selectedElement && (
        <div className="flex items-center gap-0.5 shrink-0 rounded bg-editor-sidebar-hover p-0.5 mr-1">
          <button
            onClick={() => onViewModeChange('single')}
            aria-pressed={viewMode === 'single'}
            aria-label={te('Mode simple page')}
            title={te('Mode simple page')}
            className={`p-1.5 rounded transition-colors ${viewMode === 'single' ? 'bg-editor-sidebar-active/25 text-editor-sidebar-active' : 'text-editor-sidebar-foreground/70 active:bg-editor-sidebar'}`}
          >
            <Square size={15} />
          </button>
          <button
            onClick={() => onViewModeChange('double')}
            aria-pressed={viewMode === 'double'}
            aria-label={te('Mode double page')}
            title={te('Mode double page')}
            className={`p-1.5 rounded transition-colors ${viewMode === 'double' ? 'bg-editor-sidebar-active/25 text-editor-sidebar-active' : 'text-editor-sidebar-foreground/70 active:bg-editor-sidebar'}`}
          >
            <Columns2 size={15} />
          </button>
        </div>
      )}

      {/* Undo / Redo / Save */}
      <div className="flex items-center gap-0 shrink-0">
        <button
          onClick={onUndo}
          disabled={!canUndo}
          className="p-1.5 rounded text-editor-sidebar-foreground disabled:opacity-20 active:bg-editor-sidebar-hover transition-colors"
          aria-label={te("Annuler")}
        >
          <Undo2 size={18} />
        </button>
        <button
          onClick={onRedo}
          disabled={!canRedo}
          className="p-1.5 rounded text-editor-sidebar-foreground disabled:opacity-20 active:bg-editor-sidebar-hover transition-colors"
          aria-label={te("Refaire")}
        >
          <Redo2 size={18} />
        </button>
        <div className="w-6 flex items-center justify-center text-editor-sidebar-foreground/50">
          {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
        </div>
      </div>
    </header>
  );
};

export default MobileTopBar;